import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box } from "@mui/material";
import Question from "../../components/Question";
import CustomSpinner from "../../components/CustomSpinner";
import { callBackendApiGet } from "../../lib/api";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";
import logDrtEvent from "../../lib/logging";

import { setUserFromAbandonedCart, markUserUpdateReady } from "../../redux/userSlice";
import { setInsuranceFromAbandonedCart } from "../../redux/insuranceSlice";
import { setStripeFromAbandonedCart } from "../../redux/stripeSlice";

import ContinueButton from "../../components/ContinueButton";

export default function AbandanedIntake() {
    const { patientID: routePatientId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);

    const didFetchRef = useRef(false);

    useEffect(() => {
        didFetchRef.current = false;
    }, [routePatientId]);

    useEffect(() => {
        let isMounted = true;

        (async () => {
            if (!routePatientId) {
                if (isMounted) setLoading(false);
                return;
            }

            if (didFetchRef.current) return;
            didFetchRef.current = true;

            try {
                if (isMounted) setLoading(true);

                const url = `/api/v1/abandoned-cart?patientID=${encodeURIComponent(routePatientId)}`;
                const res = await callBackendApiGet(url);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const data = await res.json();
                if (!isMounted) return;

                if (data?.has_abandoned_cart === false) {
                    setAlreadySubmitted(true);
                    return;
                }

                dispatch(setUserFromAbandonedCart(data));
                dispatch(setInsuranceFromAbandonedCart(data));
                dispatch(setStripeFromAbandonedCart(data));

                const nextPage = data?.current_page || "/concern";
                const samePath = nextPage === location.pathname;

                setTimeout(() => dispatch(markUserUpdateReady()), 2000);

                setPageAndDeferNavigate(dispatch, nextPage, () =>
                    navigate(nextPage, { replace: samePath })
                );
            } catch (err) {
                if (!isMounted) return;
                logDrtEvent(`Abandoned cart: Error fetching abandoned cart: ${err}`);
                navigate("/error");
            } finally {
                if (isMounted) setLoading(false);
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [routePatientId, dispatch, navigate, location.pathname]);

    if (loading) return <CustomSpinner />;

    if (alreadySubmitted) {
        return (
            <Question
                noBack
                title="You're all set!"
                subtitle="It looks like the intake was already submitted. If you want to start a new one, continue below."
                input={
                    <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                        <ContinueButton
                            title="Start a new intake"
                            onClick={() => navigate("/concern")}
                            selected
                            sx={{ width: "100%", maxWidth: 400 }}
                        />
                    </Box>
                }
            />
        );
    }

    return null;
}
